import { createSlice } from "@reduxjs/toolkit";
import {
  searchFoods,
  fetchMyUndesirables,
  addUndesirable,
  removeUndesirable,
} from "../actions/undesirableFoodsActions";

const initialState = {
  // Search / browse results (array of paginated blocks)
  searchResults: [],
  searchLoading: false,
  searchError: null,

  // User's personal exclusion list
  myList: [],
  myListLoading: false,
  myListError: null,

  // Breadcrumb for category navigation
  breadcrumb: [], // [{ id, name }]

  // Add/remove in-flight tracking
  pendingIds: {}, // { [element_id]: true } — optimistic UI
};

const undesirableFoodsSlice = createSlice({
  name: "undesirableFoods",
  initialState,
  reducers: {
    pushBreadcrumb: (state, action) => {
      state.breadcrumb.push(action.payload); // { id, name }
    },
    popBreadcrumb: (state) => {
      state.breadcrumb.pop();
    },
    resetBreadcrumb: (state) => {
      state.breadcrumb = [];
    },
    clearSearch: (state) => {
      state.searchResults = [];
      state.searchError = null;
    },
  },
  extraReducers: (builder) => {
    // ── Search ──
    builder.addCase(searchFoods.pending, (state) => {
      state.searchLoading = true;
      state.searchError = null;
    });
    builder.addCase(searchFoods.fulfilled, (state, action) => {
      state.searchLoading = false;
      state.searchResults = action.payload.data;
    });
    builder.addCase(searchFoods.rejected, (state, action) => {
      state.searchLoading = false;
      state.searchError = action.payload;
    });

    // ── My list ──
    builder.addCase(fetchMyUndesirables.pending, (state) => {
      state.myListLoading = true;
      state.myListError = null;
    });
    builder.addCase(fetchMyUndesirables.fulfilled, (state, action) => {
      state.myListLoading = false;
      state.myList = Array.isArray(action.payload) ? action.payload : [];
    });
    builder.addCase(fetchMyUndesirables.rejected, (state, action) => {
      state.myListLoading = false;
      state.myListError = action.payload;
    });

    // ── Add ──
    builder.addCase(addUndesirable.pending, (state, action) => {
      const eid = action.meta.arg.element_id;
      state.pendingIds[eid] = true;
    });
    builder.addCase(addUndesirable.fulfilled, (state, action) => {
      const eid = action.meta.arg.element_id;
      delete state.pendingIds[eid];
      // Add to my list
      if (action.payload?.id) {
        state.myList.push({
          id: action.payload.id,
          source: action.meta.arg.source,
          element_id: eid,
          element_name: action.payload.element_name || "",
        });
      }
    });
    builder.addCase(addUndesirable.rejected, (state, action) => {
      const eid = action.meta.arg.element_id;
      delete state.pendingIds[eid];
    });

    // ── Remove ──
    builder.addCase(removeUndesirable.pending, (state, action) => {
      const id = action.meta.arg;
      const item = state.myList.find((i) => i.id === id);
      if (item) state.pendingIds[item.element_id] = true;
    });
    builder.addCase(removeUndesirable.fulfilled, (state, action) => {
      const id = action.payload.id;
      const item = state.myList.find((i) => i.id === id);
      if (item) delete state.pendingIds[item.element_id];
      state.myList = state.myList.filter((i) => i.id !== id);
    });
    builder.addCase(removeUndesirable.rejected, (state, action) => {
      const id = action.meta.arg;
      const item = state.myList.find((i) => i.id === id);
      if (item) delete state.pendingIds[item.element_id];
    });
  },
});

export const { pushBreadcrumb, popBreadcrumb, resetBreadcrumb, clearSearch } =
  undesirableFoodsSlice.actions;
export default undesirableFoodsSlice.reducer;
